import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Check,
  ArrowRight,
  Sparkles,
  Loader2,
  Eye,
  MousePointerClick,
  TrendingUp,
  Mail,
  Repeat,
  FileText,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo/BrandLogo";

import "./Sponsor.css";

function priceLabel(p) {
  const amt = `$${Math.round(p.price).toLocaleString()}`;
  if (p.cadence === "annual") return { big: amt, small: "per year" };
  if (p.scope === "event") return { big: amt, small: "per event" };
  return { big: amt, small: "per table" };
}

/* ---------------------------
   STATIC DATA (no APIs)
---------------------------- */
const nowMs = Date.now();
const iso = (ms) => new Date(ms).toISOString();

const STATIC_PACKAGES = [
  {
    id: "main",
    name: "Main sponsor",
    blurb:
      "Be the headline brand on a single event: hero placement, email inclusion, and agenda surfaces.",
    price: 12000,
    scope: "event",
    cadence: "",
    inventory_label: "1 slot per event",
    perks: [
      "Event hero placement",
      "Sponsor mention in event emails",
      "Logo on agenda + recap",
      "Priority table placement",
    ],
  },
  {
    id: "table",
    name: "Table sponsor",
    blurb:
      "Sponsor one curated table — your brand sits at the conversation edge where decisions happen.",
    price: 4800,
    scope: "table",
    cadence: "",
    inventory_label: "Up to 3 sponsors per table",
    perks: [
      "Logo on table card",
      "Seat plate branding",
      "Included in room recap",
      "Optional moderator credit",
    ],
  },
  {
    id: "global",
    name: "Global",
    blurb: "Year-round presence across every vmeetio surface. One slot only.",
    price: 36000,
    scope: "global",
    cadence: "annual",
    remaining: 1,
    inventory_label: "1 slot total",
    perks: [
      "Footer strip on all pages",
      "Masthead on sponsorship calendar",
      "Welcome video overlay placement",
      "Included in every event email",
    ],
  },
];

const STATIC_EVENTS = [
  {
    id: 1,
    title: "AI After Hours",
    start_time: iso(nowMs + 5 * 24 * 60 * 60 * 1000),
    end_time: iso(nowMs + 5 * 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000),
    timezone: "UTC",
    description: "Inference economics, evals, and what actually ships.",
    is_published: true,
    is_open_for_sponsorship: true,
  },
  {
    id: 2,
    title: "Climate Capital Salon",
    start_time: iso(nowMs + 12 * 24 * 60 * 60 * 1000),
    end_time: iso(nowMs + 12 * 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000),
    timezone: "UTC",
    description: "Founder–investor tables on deployment and financing.",
    is_published: true,
    is_open_for_sponsorship: true,
  },
  {
    id: 3,
    title: "Designers, Off the Record",
    start_time: iso(nowMs + 18 * 24 * 60 * 60 * 1000),
    end_time: iso(nowMs + 18 * 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000),
    timezone: "UTC",
    description: "Systems, taste, hiring — candid conversations with leaders.",
    is_published: true,
    is_open_for_sponsorship: false,
  },
  {
    id: 4,
    title: "The Founders' Round",
    start_time: iso(nowMs + 26 * 24 * 60 * 60 * 1000),
    end_time: iso(nowMs + 26 * 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000),
    timezone: "UTC",
    description:
      "A structured room for growth, financing, and operator trade-offs.",
    is_published: true,
    is_open_for_sponsorship: true,
  },
];

const STATIC_TABLES = [
  {
    id: 101,
    event_id: 1,
    circle: "AI",
    topic: "Open-weight inference economics (real numbers)",
  },
  {
    id: 102,
    event_id: 1,
    circle: "AI",
    topic: "Evals that catch regressions before prod does",
  },
  {
    id: 103,
    event_id: 1,
    circle: "Founders",
    topic: "Shipping AI features without losing the roadmap",
  },

  {
    id: 201,
    event_id: 2,
    circle: "Climate",
    topic: "Term sheets, board dynamics, flat rounds",
  },
  {
    id: 202,
    event_id: 2,
    circle: "Investors",
    topic: "What gets funded in climate infra now",
  },
  {
    id: 203,
    event_id: 2,
    circle: "Operators",
    topic: "Deployment: procurement, pilots, and proof",
  },

  {
    id: 301,
    event_id: 3,
    circle: "Design",
    topic: "Design systems at scale (what breaks first)",
  },
  {
    id: 302,
    event_id: 3,
    circle: "Design",
    topic: "Taste: feedback that doesn’t destroy the team",
  },

  {
    id: 401,
    event_id: 4,
    circle: "Founders",
    topic: "Bridge rounds + optionality: the new leverage",
  },
  {
    id: 402,
    event_id: 4,
    circle: "Operators",
    topic: "Enterprise GTM: past $1M ARR",
  },
];

const STATIC_SPONSORSHIPS = [
  {
    id: "ev-1",
    scope: "event",
    event_id: 2,
    company: "Greentide",
    logo: "",
    status: "approved",
  },
  {
    id: "t-1",
    scope: "table",
    table_id: 101,
    company: "Anyon AI",
    logo: "",
    status: "approved",
  },
  {
    id: "t-2",
    scope: "table",
    table_id: 201,
    company: "SignalWorks",
    logo: "",
    status: "approved",
  },
  {
    id: "t-3",
    scope: "table",
    table_id: 201,
    company: "North Capital",
    logo: "",
    status: "approved",
  },
];

const REFERRERS = {
  NORTH10: { company: "North Capital", discount_pct: 10 },
  GREEN10: { company: "Greentide", discount_pct: 10 },
};

function buildAvailability(eventId) {
  const id = Number(eventId);
  const ev = STATIC_EVENTS.find((e) => e.id === id) || null;

  const mainSponsor =
    STATIC_SPONSORSHIPS.find((s) => s.scope === "event" && s.event_id === id) ||
    null;

  const tables = STATIC_TABLES.filter((t) => t.event_id === id).map((t) => {
    const sponsors = STATIC_SPONSORSHIPS.filter(
      (s) => s.scope === "table" && s.table_id === t.id,
    ).map((s) => ({
      company: s.company,
      logo: s.logo || "",
      status: s.status || "approved",
    }));
    const sponsor_slots_taken = sponsors.length;
    return {
      id: t.id,
      circle: t.circle,
      topic: t.topic,
      sponsors,
      sponsor_slots_taken,
      is_available_for_sponsorship: sponsor_slots_taken < 3,
    };
  });

  const available_tables = tables.filter((t) => t.is_available_for_sponsorship);

  return {
    is_open_for_sponsorship: !!ev?.is_open_for_sponsorship,
    main_sponsor_taken: !!mainSponsor,
    main_sponsor: mainSponsor
      ? {
          company: mainSponsor.company,
          logo: mainSponsor.logo || "",
          status: mainSponsor.status || "approved",
        }
      : null,
    tables,
    available_tables,
    tables_open: available_tables.length,
  };
}

/* ---------------------------
   UI Components
---------------------------- */

function PackageCard({ pkg, onChoose }) {
  const { big, small } = priceLabel(pkg);
  const sold_out =
    pkg.remaining !== undefined && pkg.remaining <= 0 && pkg.id === "global";

  const toneClass =
    pkg.id === "global"
      ? "pkgCard--global"
      : pkg.id === "main"
        ? "pkgCard--main"
        : "pkgCard--table";

  const pillToneClass =
    pkg.id === "global"
      ? "pkgPill--global"
      : pkg.id === "main"
        ? "pkgPill--main"
        : "pkgPill--table";

  return (
    <div data-testid={`pkg-${pkg.id}`} className={`pkgCard ${toneClass}`}>
      {pkg.id === "main" && (
        <span className="pkgBadgeMostChosen">Most chosen</span>
      )}

      <p className="overline pkgOverline">
        {pkg.id === "global" && <Sparkles size={11} className="pkgSparkle" />}
        {pkg.name}
      </p>

      <p className="pkgBlurb">{pkg.blurb}</p>

      <div className="pkgPriceRow">
        <span
          className="font-display pkgPriceBig"
          data-testid={`pkg-${pkg.id}-price`}
        >
          {big}
        </span>
        <span className="pkgPriceSmall">{small}</span>
      </div>

      {pkg.id === "global" && (
        <p
          className="pkgAutoRenewNote"
          data-testid="pkg-global-auto-renew-note"
        >
          Auto-renews yearly · cancel anytime
        </p>
      )}

      <span
        className={`pkgInventoryPill ${pillToneClass}`}
        data-testid={`pkg-${pkg.id}-inventory`}
      >
        {pkg.inventory_label}
      </span>

      <ul className="pkgPerks">
        {pkg.perks.map((perk, i) => (
          <li key={i} className="pkgPerkItem">
            <Check size={14} className="pkgCheck" />
            <span>{perk}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={() => onChoose(pkg)}
        disabled={sold_out}
        data-testid={`pkg-${pkg.id}-cta`}
        className="btn-gold pkgCta"
        type="button"
      >
        {sold_out ? (
          "All slots taken"
        ) : (
          <>
            Get started <ArrowRight size={14} />
          </>
        )}
      </button>
    </div>
  );
}

function CheckoutDialog({ pkg, onClose }) {
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    contact_email: "",
    logo_url: "",
    website: "",
    event_id: "",
    table_id: "",
    logo_use_authorised: false,
    agree_disclaimer: false,
  });
  const [events] = useState(
    STATIC_EVENTS.filter((e) => e.is_published !== false),
  );
  const [avail, setAvail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoRenew, setAutoRenew] = useState(true);

  useEffect(() => {
    if (
      !pkg ||
      (pkg.scope !== "event" && pkg.scope !== "table") ||
      !form.event_id
    ) {
      setAvail(null);
      return;
    }
    setAvail(buildAvailability(form.event_id));
  }, [pkg, form.event_id]);

  if (!pkg) return null;

  const referrer = pkg._referrer || null;
  const discountPct = referrer ? referrer.discount_pct : 0;
  const finalPrice =
    discountPct > 0
      ? Math.round(pkg.price * (1 - discountPct / 100))
      : Math.round(pkg.price);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      toast.success("Static demo: checkout started.");
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const needsEvent = pkg.scope === "event" || pkg.scope === "table";
  const needsTable = pkg.scope === "table";
  const mainTaken = avail?.main_sponsor_taken && pkg.scope === "event";

  return (
    <div
      data-testid="sponsorship-checkout-modal"
      className="sponsorModalBackdrop"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="sponsorDialog"
      >
        <p className="overline sponsorDialogOverline">Sponsor checkout</p>
        <h3 className="font-display sponsorDialogTitle">{pkg.name}</h3>

        <p className="sponsorDialogPriceLine">
          {discountPct > 0 ? (
            <>
              <span className="sponsorStrike">
                ${Math.round(pkg.price).toLocaleString()}
              </span>
              <span className="sponsorDiscountPrice">
                ${finalPrice.toLocaleString()}
              </span>
            </>
          ) : (
            <>${Math.round(pkg.price).toLocaleString()}</>
          )}
          {" · "}
          {pkg.cadence === "annual"
            ? "per year"
            : pkg.scope === "event"
              ? "per event"
              : "per table"}
        </p>

        {referrer && (
          <div
            className="sponsorReferralPill"
            data-testid="dialog-referral-pill"
          >
            🎁{" "}
            <strong className="sponsorReferralPct">{discountPct}% off</strong>{" "}
            via referral from {referrer.company}
          </div>
        )}

        <div className="sponsorDialogFields">
          <input
            className="sponsorDialogInput"
            required
            placeholder="Company name"
            value={form.company}
            onChange={set("company")}
            data-testid="sponsor-company"
          />
          <input
            className="sponsorDialogInput"
            required
            placeholder="Contact name"
            value={form.contact_name}
            onChange={set("contact_name")}
            data-testid="sponsor-contact-name"
          />
          <input
            className="sponsorDialogInput"
            required
            type="email"
            placeholder="Contact email"
            value={form.contact_email}
            onChange={set("contact_email")}
            data-testid="sponsor-contact-email"
          />
          <input
            className="sponsorDialogInput"
            placeholder="Logo URL — public PNG link with transparent background"
            value={form.logo_url}
            onChange={set("logo_url")}
            data-testid="sponsor-logo-url"
          />
          <input
            className="sponsorDialogInput"
            placeholder="Website (optional)"
            value={form.website}
            onChange={set("website")}
            data-testid="sponsor-website"
          />

          {needsEvent && (
            <select
              className="sponsorDialogSelect"
              required
              value={form.event_id}
              onChange={set("event_id")}
              data-testid="sponsor-event-select"
            >
              <option value="">Pick an event…</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title}
                </option>
              ))}
            </select>
          )}

          {needsTable && form.event_id && (
            <select
              className="sponsorDialogSelect"
              required
              value={form.table_id}
              onChange={set("table_id")}
              data-testid="sponsor-table-select"
            >
              <option value="">Pick a table…</option>
              {(avail?.available_tables || []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.circle} — {t.topic}
                </option>
              ))}
            </select>
          )}

          {needsTable &&
            form.event_id &&
            avail &&
            (avail.available_tables || []).length === 0 && (
              <p className="sponsorDialogWarnGold">
                All tables on this event are already sponsored. Pick another
                event.
              </p>
            )}

          {mainTaken && (
            <p className="sponsorDialogWarnGold">
              This event already has a Main sponsor. Pick another event.
            </p>
          )}
        </div>

        {pkg.id === "global" && (
          <label
            data-testid="sponsor-auto-renew"
            className="sponsorAutoRenewCard"
          >
            <input
              type="checkbox"
              checked={autoRenew}
              onChange={(e) => setAutoRenew(e.target.checked)}
              className="sponsorCheckbox"
              data-testid="sponsor-auto-renew-input"
            />
            <div className="sponsorAutoRenewText">
              <p className="sponsorAutoRenewTitle">
                Auto-renew yearly{" "}
                <span className="sponsorGold">· cancel anytime</span>
              </p>
              <p className="sponsorAutoRenewBody">
                {autoRenew
                  ? `Stripe will charge $${finalPrice.toLocaleString()} every year on this date until you cancel from your sponsor dashboard. Recommended — keeps your logo live across every vmeetio surface without lapses.`
                  : "Uncheck for a single yearly charge with no auto-renewal. We'll email you 30 days before expiry."}
              </p>
            </div>
          </label>
        )}

        <div className="sponsorDeclarations" data-testid="sponsor-declarations">
          <label className="sponsorDeclCard">
            <input
              type="checkbox"
              checked={form.logo_use_authorised}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  logo_use_authorised: e.target.checked,
                }))
              }
              className="sponsorCheckbox"
              data-testid="sponsor-logo-auth"
            />
            <div className="sponsorDeclText">
              <p className="sponsorDeclTitle">
                I'm authorised to use this logo
              </p>
              <p className="sponsorDeclBody">
                I confirm I have permission from{" "}
                <strong>{form.company || "the company above"}</strong> to grant
                vmeetio the right to display its logo on the chosen event
                surfaces (event card, table card, dossier, footer strip) for the
                duration of this sponsorship.
              </p>
            </div>
          </label>

          <label className="sponsorDeclCard">
            <input
              type="checkbox"
              checked={form.agree_disclaimer}
              onChange={(e) =>
                setForm((f) => ({ ...f, agree_disclaimer: e.target.checked }))
              }
              className="sponsorCheckbox"
              data-testid="sponsor-disclaimer"
            />
            <div className="sponsorDeclText">
              <p className="sponsorDeclTitle">
                I've read the sponsor disclaimer
              </p>
              <p className="sponsorDeclBody">
                Sponsorship is an impressions-and-association deal — it does not
                give my company any right to attendee personal data, no implied
                endorsement by vmeetio members, and no editorial control over
                event content. Refunds follow vmeetio's standard sponsor terms.
              </p>
            </div>
          </label>
        </div>

        <div className="sponsorDialogActions">
          <button
            type="submit"
            disabled={
              busy ||
              mainTaken ||
              (needsTable &&
                form.event_id &&
                (avail?.available_tables || []).length === 0) ||
              !form.logo_use_authorised ||
              !form.agree_disclaimer
            }
            data-testid="sponsor-checkout-submit"
            className="btn-gold sponsorPayBtn"
          >
            {busy ? (
              <>
                <Loader2 className="sponsorSpin" size={14} /> Working…
              </>
            ) : (
              <>Pay ${finalPrice.toLocaleString()}</>
            )}
          </button>

          <button type="button" onClick={onClose} className="sponsorCancelBtn">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Sponsor() {
  const [packages] = useState(STATIC_PACKAGES);
  const [choice, setChoice] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const refCode = (searchParams.get("ref") || "").toUpperCase();
  const [referrer, setReferrer] = useState(null);

  useEffect(() => {
    if (!refCode) return setReferrer(null);
    setReferrer(REFERRERS[refCode] || null);
  }, [refCode]);

  useEffect(() => {
    if (searchParams.get("canceled")) {
      toast.error("Checkout canceled. Try again whenever you're ready.");
      const n = new URLSearchParams(searchParams);
      n.delete("canceled");
      setSearchParams(n, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <div
      data-testid="sponsor-page"
      className="sponsorPage"
      style={{
        background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
      }}
    >
      <div className="container-x sponsorContainer">
        <div className="sponsorIntro">
          <p className="overline sponsorIntroOverline">Sponsor vmeetio</p>
          <h1 className="font-display sponsorIntroTitle">
            Sponsor an event, or anchor the year.
          </h1>
          <p className="sponsorIntroBody">
            Pick a specific upcoming event below and choose{" "}
            <strong className="sponsorStrong">whole-event</strong> or{" "}
            <strong className="sponsorStrong">table-level</strong> sponsorship —
            you see exactly who else is on the room before you commit. Or scroll
            to the year-round Global tier.
          </p>

          {referrer && (
            <div
              data-testid="referral-banner"
              className="sponsorReferralBanner"
            >
              <Sparkles size={14} className="sponsorReferralIcon" />
              <span>
                Recommended by{" "}
                <strong className="sponsorReferralCompany">
                  {referrer.company}
                </strong>{" "}
                — you get{" "}
                <strong className="sponsorReferralCompany">
                  {referrer.discount_pct}% off
                </strong>{" "}
                at checkout.
              </span>
            </div>
          )}
        </div>

        <div className="sponsorPkgGrid" data-testid="sponsorship-packages">
          {packages
            .filter((p) => p.id === "main" || p.id === "table")
            .map((p) => (
              <PackageCard
                key={p.id}
                pkg={p}
                onChoose={(pkg) =>
                  setChoice({
                    ...pkg,
                    _ref_code: refCode || "",
                    _referrer: referrer,
                  })
                }
              />
            ))}
        </div>

        <SponsorEventPicker
          packages={packages}
          refCode={refCode}
          referrer={referrer}
        />

        <div
          className="sponsorGlobalHeader"
          data-testid="sponsor-global-section"
        >
          <p className="overline sponsorGlobalOverline">Year-round presence</p>
          <h2 className="font-display sponsorGlobalTitle">
            Or anchor every vmeetio room.
          </h2>
          <p className="sponsorGlobalBody">
            Global is the only auto-renewing tier — your logo lives in the
            footer strip, the sponsorship calendar masthead, every welcome
            video, and every event email. One slot only.
          </p>
        </div>

        <div
          className="sponsorGlobalCardWrap"
          data-testid="sponsor-global-card-wrap"
        >
          {packages
            .filter((p) => p.id === "global")
            .map((p) => (
              <PackageCard
                key={p.id}
                pkg={p}
                onChoose={(pkg) =>
                  setChoice({
                    ...pkg,
                    _ref_code: refCode || "",
                    _referrer: referrer,
                  })
                }
              />
            ))}
        </div>

        <div className="sponsorFinePrint">
          Need something custom? Email{" "}
          <a href="mailto:sponsors@vmeetio.io" className="sponsorGoldLink">
            sponsors@vmeetio.io
          </a>{" "}
          — we'll build a package around your campaign.
        </div>

        <div className="sponsorFinePrint sponsorAlready">
          Already a sponsor?{" "}
          <Link
            to="/sponsor-dashboard"
            className="sponsorDashLink"
            data-testid="sponsor-dashboard-link"
          >
            View your impression dashboard
          </Link>
        </div>

        <ProofStrip />
        <TrustedBy />
        <SponsorshipCalendarPreview />
        <SponsorFAQ />

        <div className="sponsorCalendarCtaWrap">
          <Link
            to="/sponsorship-calendar"
            className="sponsorCalendarCta"
            data-testid="sponsor-calendar-cta"
          >
            See the full sponsorship calendar <ArrowRight size={12} />
          </Link>
        </div>
      </div>

      <CheckoutDialog pkg={choice} onClose={() => setChoice(null)} />
    </div>
  );
}

/* ---- Sections ---- */

function ProofStrip() {
  const stats = [
    {
      icon: <Eye size={16} />,
      value: "12,400+",
      label: "monthly logo impressions per event sponsor",
    },
    {
      icon: <MousePointerClick size={16} />,
      value: "3.8%",
      label: "average click-through on the welcome video card",
    },
    {
      icon: <TrendingUp size={16} />,
      value: "₽ 4.2x",
      label: "median pipeline lift reported by Q4 sponsors",
    },
    {
      icon: <Mail size={16} />,
      value: "94%",
      label: "open rate on sponsor-branded confirmation emails",
    },
  ];

  return (
    <section
      className="sponsorSection sponsorProof"
      data-testid="sponsor-proof-strip"
    >
      <p className="overline sponsorSectionOverlineGold">
        Why brands sponsor vmeetio
      </p>
      <h2 className="font-display sponsorSectionTitleCentered">
        Logos that land in front of the right rooms.
      </h2>

      <div className="sponsorProofGrid">
        {stats.map((s, i) => (
          <div
            key={i}
            data-testid={`sponsor-proof-${i}`}
            className="glass sponsorProofCard"
          >
            <span className="sponsorProofIconBox">{s.icon}</span>
            <p className="font-display sponsorProofValue">{s.value}</p>
            <p className="sponsorProofLabel">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function TrustedBy() {
  const brands = (() => {
    const seen = new Set();
    const uniq = (STATIC_SPONSORSHIPS || []).filter((s) => {
      const c = (s.company || "").trim();
      if (!c || seen.has(c)) return false;
      seen.add(c);
      return s.status === "approved";
    });
    return uniq.slice(0, 10);
  })();

  if (brands.length === 0) return null;

  return (
    <section
      className="sponsorSection sponsorTrusted"
      data-testid="sponsor-trusted-by"
    >
      <p className="overline sponsorTrustedOverline">Trusted by</p>
      <p className="sponsorTrustedSub">
        Brands currently sharing vmeetio rooms with category-defining operators.
      </p>

      <div className="sponsorTrustedGrid">
        {brands.map((s) => (
          <Link
            key={`${s.id}-${s.company}`}
            to={`/brands/${encodeURIComponent(s.company)}`}
            data-testid={`sponsor-trusted-${s.company}`}
            className="sponsorTrustedPill"
          >
            <BrandLogo company={s.company} logo={s.logo} size={22} />
            <span className="sponsorTrustedName">{s.company}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function SponsorEventPicker({ packages, refCode, referrer }) {
  const [searchParams] = useSearchParams();
  const preselectId = searchParams.get("event") || "";
  const [events] = useState(
    STATIC_EVENTS.filter((e) => e.is_published !== false),
  );
  const [eventId, setEventId] = useState(preselectId);
  const [ctx, setCtx] = useState(null);
  const [cart, setCart] = useState([]);
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    contact_email: "",
    logo_url: "",
    website: "",
    logo_use_authorised: false,
    agree_disclaimer: false,
  });
  const [busy, setBusy] = useState(false);
  const sectionRef = useRef(null);

  const tablePkg = packages.find((p) => p.id === "table");
  const mainPkg = packages.find((p) => p.id === "main");

  useEffect(() => {
    if (preselectId && sectionRef.current) {
      const t = setTimeout(() => {
        sectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 250);
      return () => clearTimeout(t);
    }
  }, [preselectId]);

  useEffect(() => {
    if (!eventId) {
      setCtx(null);
      return;
    }
    setCtx(buildAvailability(eventId));
  }, [eventId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addMain = () => {
    if (!ctx) return;
    if (ctx.main_sponsor_taken) {
      toast.error("Main slot already taken.");
      return;
    }
    if (cart.some((c) => c.package_id === "main" && c.event_id === eventId)) {
      toast("Main slot already in cart for this event.");
      return;
    }
    setCart((c) => [
      ...c,
      { package_id: "main", event_id: eventId, table_id: "" },
    ]);
    toast.success("Main event slot added to cart.");
  };

  const addTable = (table) => {
    if (cart.some((c) => c.package_id === "table" && c.table_id === table.id)) {
      toast("Table already in cart.");
      return;
    }
    setCart((c) => [
      ...c,
      {
        package_id: "table",
        event_id: eventId,
        table_id: table.id,
        _topic: table.topic,
        sponsor_table_name: "",
        sponsor_moderator_name: "",
        sponsor_moderator_bio: "",
        sponsor_moderator_avatar: "",
        sponsor_moderator_linkedin: "",
      },
    ]);
  };

  const removeItem = (i) => setCart((c) => c.filter((_, j) => j !== i));
  const updateItem = (i, patch) =>
    setCart((c) => c.map((it, j) => (j === i ? { ...it, ...patch } : it)));

  const total = cart.reduce(
    (s, it) =>
      s +
      (it.package_id === "main" ? mainPkg?.price || 0 : tablePkg?.price || 0),
    0,
  );

  const checkout = async () => {
    if (!cart.length) return toast.error("Add at least one slot to the cart.");
    if (!form.company || !form.contact_name || !form.contact_email)
      return toast.error("Fill company + contact details.");
    if (!form.logo_use_authorised)
      return toast.error("Please confirm you're authorised to use the logo.");
    if (!form.agree_disclaimer)
      return toast.error("Please agree to the sponsor disclaimer.");
    setBusy(true);
    try {
      toast.success("Static demo: cart checkout started.");
      setCart([]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      ref={sectionRef}
      className="sponsorEventPicker"
      data-testid="sponsor-event-picker"
    >
      <div className="sponsorEventPickerHeader">
        <p className="overline sponsorSectionOverlineGold">Sponsor an event</p>
        <h2 className="font-display sponsorEventPickerTitle">
          Pick the event you want in the room with.
        </h2>
      </div>

      <div className="glass sponsorEventPickerCard">
        <label className="sponsorLabelBlock">
          <span className="overline sponsorLabel">
            Choose an upcoming event
          </span>
          <select
            value={eventId}
            onChange={(e) => {
              setEventId(e.target.value);
              setCart([]);
            }}
            data-testid="sponsor-event-select"
            className="sponsorEventSelect"
          >
            <option value="" className="sponsorOptionDark">
              — select event —
            </option>
            {events.map((e) => (
              <option key={e.id} value={e.id} className="sponsorOptionDark">
                {e.title}
              </option>
            ))}
          </select>
        </label>

        {ctx && !ctx.is_open_for_sponsorship && (
          <div
            className="sponsorClosedBanner"
            data-testid="event-closed-banner"
          >
            The organiser has closed this event for new sponsorships. Pick
            another event or reach out at{" "}
            <a className="sponsorUnderline" href="mailto:sponsors@vmeetio.io">
              sponsors@vmeetio.io
            </a>
            .
          </div>
        )}

        {ctx && ctx.is_open_for_sponsorship && (
          <div className="sponsorPickerSections">
            <div
              className="sponsorMainStatus"
              data-testid="sponsor-main-status"
            >
              <p className="overline sponsorMainOverline">Main event sponsor</p>

              {ctx.main_sponsor_taken ? (
                <div className="sponsorMainTakenRow">
                  {ctx.main_sponsor.logo ? (
                    <img
                      src={ctx.main_sponsor.logo || undefined}
                      alt={ctx.main_sponsor.company}
                      className="sponsorMainLogo"
                    />
                  ) : (
                    <span className="sponsorMainFallbackLetter">
                      {(ctx.main_sponsor.company || "?").slice(0, 1)}
                    </span>
                  )}

                  <span className="sponsorMainHeldText">
                    Held by{" "}
                    <strong className="sponsorMainHeldCompany">
                      {ctx.main_sponsor.company}
                    </strong>
                  </span>

                  <span className="sponsorStatusPill">
                    {ctx.main_sponsor.status}
                  </span>
                </div>
              ) : (
                <div className="sponsorMainOpenRow">
                  <p className="sponsorMainOpenText">
                    Open · be the headline brand on this event.
                  </p>

                  <button
                    type="button"
                    onClick={addMain}
                    data-testid="add-main-to-cart"
                    className="sponsorAddMainBtn"
                  >
                    + Add to cart · ${mainPkg?.price?.toLocaleString()}
                  </button>
                </div>
              )}
            </div>

            <div className="sponsorTableList" data-testid="sponsor-table-list">
              <p className="overline sponsorTableOverline">
                Tables · up to 3 sponsors each
              </p>

              <div className="sponsorTableItems">
                {(ctx.tables || []).map((t) => {
                  const inCart = cart.some(
                    (c) => c.package_id === "table" && c.table_id === t.id,
                  );

                  return (
                    <div
                      key={t.id}
                      data-testid={`sponsor-table-${t.id}`}
                      className="sponsorTableRow"
                    >
                      <div className="sponsorTableRowLeft">
                        <p className="sponsorTableTopic">{t.topic}</p>
                        <p className="sponsorTableMeta">
                          {t.sponsor_slots_taken} of 3 sponsor slots taken
                          {(t.sponsors || []).length > 0 && (
                            <span>
                              {" "}
                              · with{" "}
                              {(t.sponsors || [])
                                .map((s) => s.company)
                                .join(", ")}
                            </span>
                          )}
                        </p>
                      </div>

                      {!t.is_available_for_sponsorship ? (
                        <span className="sponsorSoldOutPill">Sold out</span>
                      ) : inCart ? (
                        <span className="sponsorInCartPill">In cart</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => addTable(t)}
                          data-testid={`add-table-${t.id}`}
                          className="sponsorAddTableBtn"
                        >
                          Add · ${tablePkg?.price?.toLocaleString()}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {cart.length > 0 && (
          <div className="sponsorCart" data-testid="sponsor-cart">
            <div className="sponsorCartHeader">
              <p className="overline sponsorCartOverline">
                Your cart · {cart.length} slot{cart.length === 1 ? "" : "s"}
              </p>
              <p className="font-display sponsorCartTotal">
                ${total.toLocaleString()}
              </p>
            </div>

            {cart.some((it) => it.package_id === "table") && (
              <div
                className="sponsorCartPreview"
                data-testid="sponsor-cart-preview"
              >
                <p className="overline sponsorCartPreviewOverline">
                  Live preview · here's what attendees will see
                </p>

                <div className="sponsorCartPreviewGrid">
                  {cart
                    .filter((it) => it.package_id === "table")
                    .slice(0, 2)
                    .map((it, i) => (
                      <SponsorshipPreviewTile
                        key={i}
                        topic={it._topic}
                        sponsorCompany={form.company || "Your brand"}
                        sponsorLogo={form.logo_url}
                        tableName={it.sponsor_table_name}
                        moderator={it.sponsor_moderator_name}
                      />
                    ))}
                </div>

                <p className="sponsorCartPreviewNote">
                  Update the table name + moderator below to refine the preview.
                  Your logo will appear once you upload it.
                </p>
              </div>
            )}

            <div className="sponsorCartItems">
              {cart.map((it, i) => (
                <div
                  key={i}
                  data-testid={`cart-item-${i}`}
                  className="sponsorCartItem"
                >
                  <div className="sponsorCartItemTop">
                    <div>
                      <p className="sponsorCartItemTitle">
                        {it.package_id === "main"
                          ? "Main event sponsor"
                          : "Table sponsor"}
                      </p>
                      {it._topic && (
                        <p className="sponsorCartItemSub">
                          Table · {it._topic}
                        </p>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => removeItem(i)}
                      data-testid={`cart-remove-${i}`}
                      className="sponsorCartRemove"
                    >
                      Remove
                    </button>
                  </div>

                  {it.package_id === "table" && (
                    <div className="sponsorCartTableFields">
                      <input
                        value={it.sponsor_table_name}
                        onChange={(e) =>
                          updateItem(i, { sponsor_table_name: e.target.value })
                        }
                        placeholder={`Proposed table name (e.g. 'Greentide · Climate financing')`}
                        data-testid={`cart-${i}-table-name`}
                        className="sponsorCartField"
                      />
                      <input
                        value={it.sponsor_moderator_name}
                        onChange={(e) =>
                          updateItem(i, {
                            sponsor_moderator_name: e.target.value,
                          })
                        }
                        placeholder="Your moderator (name)"
                        data-testid={`cart-${i}-mod-name`}
                        className="sponsorCartField"
                      />
                      <input
                        value={it.sponsor_moderator_linkedin}
                        onChange={(e) =>
                          updateItem(i, {
                            sponsor_moderator_linkedin: e.target.value,
                          })
                        }
                        placeholder="Moderator LinkedIn (optional)"
                        data-testid={`cart-${i}-mod-linkedin`}
                        className="sponsorCartField"
                      />
                      <input
                        value={it.sponsor_moderator_avatar}
                        onChange={(e) =>
                          updateItem(i, {
                            sponsor_moderator_avatar: e.target.value,
                          })
                        }
                        placeholder="Moderator photo URL (optional)"
                        data-testid={`cart-${i}-mod-avatar`}
                        className="sponsorCartField"
                      />
                      <textarea
                        value={it.sponsor_moderator_bio}
                        onChange={(e) =>
                          updateItem(i, {
                            sponsor_moderator_bio: e.target.value,
                          })
                        }
                        rows={2}
                        placeholder="Moderator one-liner (covered by your sponsorship fee — listed vmeetio moderators must be engaged off-platform)"
                        data-testid={`cart-${i}-mod-bio`}
                        className="sponsorCartField sponsorCartTextarea"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="sponsorCartBuyerGrid">
              <input
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                placeholder="Company"
                data-testid="cart-company"
                className="sponsorCartField"
              />
              <input
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
                placeholder="Website (optional)"
                data-testid="cart-website"
                className="sponsorCartField"
              />
              <input
                value={form.contact_name}
                onChange={(e) => set("contact_name", e.target.value)}
                placeholder="Your name"
                data-testid="cart-contact-name"
                className="sponsorCartField"
              />
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => set("contact_email", e.target.value)}
                placeholder="Your email"
                data-testid="cart-contact-email"
                className="sponsorCartField"
              />
              <input
                value={form.logo_url}
                onChange={(e) => set("logo_url", e.target.value)}
                placeholder="Logo URL (PNG, transparent)"
                data-testid="cart-logo-url"
                className="sponsorCartField sponsorCartFieldSpan2"
              />
            </div>

            <label className="sponsorCartCheckRow">
              <input
                type="checkbox"
                checked={form.logo_use_authorised}
                onChange={(e) => set("logo_use_authorised", e.target.checked)}
                className="sponsorCheckbox"
                data-testid="cart-logo-auth"
              />
              <span>
                I confirm I'm authorised to use the company logo above for the
                chosen surfaces.
              </span>
            </label>

            <label className="sponsorCartCheckRow sponsorCartCheckRowTight">
              <input
                type="checkbox"
                checked={form.agree_disclaimer}
                onChange={(e) => set("agree_disclaimer", e.target.checked)}
                className="sponsorCheckbox"
                data-testid="cart-disclaimer"
              />
              <span>
                I've read the sponsor disclaimer — no attendee data harvesting,
                no implied endorsement, no editorial control. Refunds per
                vmeetio terms.
              </span>
            </label>

            <button
              type="button"
              onClick={checkout}
              disabled={busy}
              data-testid="cart-checkout"
              className="btn-gold sponsorCartCheckout"
            >
              {busy
                ? "Working…"
                : `Checkout ${cart.length} slot${cart.length === 1 ? "" : "s"} · $${total.toLocaleString()}`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function SponsorshipCalendarPreview() {
  const rows = [
    {
      id: 1,
      title: STATIC_EVENTS.find((e) => e.id === 1)?.title,
      start_time: STATIC_EVENTS.find((e) => e.id === 1)?.start_time,
      tables_open: buildAvailability(1).tables_open,
      event_slot: { lead_sponsor: null },
    },
    {
      id: 2,
      title: STATIC_EVENTS.find((e) => e.id === 2)?.title,
      start_time: STATIC_EVENTS.find((e) => e.id === 2)?.start_time,
      tables_open: buildAvailability(2).tables_open,
      event_slot: { lead_sponsor: { company: "Greentide" } },
    },
    {
      id: 3,
      title: STATIC_EVENTS.find((e) => e.id === 3)?.title,
      start_time: STATIC_EVENTS.find((e) => e.id === 3)?.start_time,
      tables_open: buildAvailability(3).tables_open,
      event_slot: { lead_sponsor: null },
    },
    {
      id: 4,
      title: STATIC_EVENTS.find((e) => e.id === 4)?.title,
      start_time: STATIC_EVENTS.find((e) => e.id === 4)?.start_time,
      tables_open: buildAvailability(4).tables_open,
      event_slot: { lead_sponsor: null },
    },
  ].slice(0, 4);

  if (rows.length === 0) return null;

  return (
    <section
      className="sponsorSection sponsorCalendar"
      data-testid="sponsor-calendar-preview"
    >
      <div className="sponsorCalendarHeader">
        <div>
          <p className="overline sponsorCalendarOverline">
            Open slots this quarter
          </p>
          <h2 className="font-display sponsorCalendarTitle">
            Pick a room before someone else does.
          </h2>
        </div>

        <Link to="/calendar" className="sponsorSeeAllSlots">
          See all slots <ArrowRight size={12} />
        </Link>
      </div>

      <div className="sponsorCalendarGrid">
        {rows.map((r) => {
          const mainSponsor = r.event_slot && r.event_slot.lead_sponsor;
          const mainTaken = !!mainSponsor;
          const tablesOpen = Number(r.tables_open || 0);

          const dateLabel = (() => {
            try {
              return new Date(r.start_time).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              });
            } catch {
              return r.start_time;
            }
          })();

          const allBooked = mainTaken && tablesOpen === 0;

          return (
            <div
              key={r.id}
              data-testid={`sponsor-calendar-row-${r.id}`}
              className={`glass sponsorCalendarRow ${allBooked ? "isBooked" : ""}`}
            >
              <div className="sponsorCalendarRowLeft">
                <p className="overline sponsorCalendarDate">{dateLabel}</p>
                <p className="font-display sponsorCalendarEventTitle">
                  {r.title}
                </p>
                <p className="sponsorCalendarMeta">
                  {mainTaken ? (
                    <>
                      Main slot held by{" "}
                      <strong className="sponsorCalendarStrong">
                        {mainSponsor.company}
                      </strong>
                      {tablesOpen > 0 && (
                        <>
                          {" "}
                          · {tablesOpen} table slot{tablesOpen === 1 ? "" : "s"}{" "}
                          open
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      Main + {tablesOpen} table slot
                      {tablesOpen === 1 ? "" : "s"} open
                    </>
                  )}
                </p>
              </div>

              {allBooked ? (
                <span className="sponsorBookedPill">Fully booked</span>
              ) : (
                <Link
                  to={`/sponsor?event=${r.id}`}
                  data-testid={`sponsor-calendar-cta-${r.id}`}
                  className="sponsorClaimBtn"
                >
                  Claim <ArrowRight size={11} />
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

const FAQ_ITEMS = [
  {
    q: "How does Stripe checkout actually work — am I locked in?",
    a: "Main and Table sponsorships are one-time charges. Global is the only auto-renewing subscription — and you can cancel anytime from the sponsor dashboard, keeping all perks until the paid-through date.",
    icon: <Repeat size={14} />,
  },
  {
    q: "Where exactly does my logo appear?",
    a: "Global → app footer, sponsorship calendar masthead, welcome video overlay, every event email. Main → event hero, agenda, every event email, .ics calendar invite. Table → table card, 3D seat plate, room recap.",
    icon: <Eye size={14} />,
  },
  {
    q: "Can I get a PDF invoice for accounts payable?",
    a: "Yes. Stripe auto-emails a receipt after every charge, and you can download a tax-compliant PDF invoice from your sponsor dashboard's billing tab.",
    icon: <FileText size={14} />,
  },
  {
    q: "Do I get a list of who attended my sponsored event?",
    a: "Main and Table sponsors receive a post-event attendee export (role, company, country). Names are only revealed for attendees who explicitly opt-in at registration. No spam-grade dumps.",
    icon: <TrendingUp size={14} />,
  },
  {
    q: "What if I want to sponsor a specific upcoming event?",
    a: "Pick 'Main event sponsor' or 'Table sponsor' on the cards above, then choose the event in the checkout. Inventory is enforced — only one Main per event, and tables are first-paid-first-locked.",
    icon: <Mail size={14} />,
  },
  {
    q: "Can I refer another brand for a discount?",
    a: "Every Global sponsor gets a referral code. Any new sponsor checking out with your code gets 10% off — and your account gets a 10% credit toward your next renewal.",
    icon: <Sparkles size={14} />,
  },
];

function SponsorFAQ() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section className="sponsorSection sponsorFaq" data-testid="sponsor-faq">
      <div className="sponsorFaqHeader">
        <p className="overline sponsorFaqOverline">Common questions</p>
        <h2 className="font-display sponsorFaqTitle">
          Answers before the awkward email.
        </h2>
      </div>

      <div className="sponsorFaqList">
        {FAQ_ITEMS.map((it, i) => {
          const isOpen = openIdx === i;

          return (
            <div
              key={i}
              data-testid={`sponsor-faq-item-${i}`}
              className={`glass sponsorFaqItem ${isOpen ? "isOpen" : ""}`}
            >
              <button
                type="button"
                onClick={() => setOpenIdx(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                data-testid={`sponsor-faq-toggle-${i}`}
                className="sponsorFaqToggle"
              >
                <span className="sponsorFaqIconBox">{it.icon}</span>
                <span className="sponsorFaqQuestion">{it.q}</span>
                <ChevronDown
                  size={16}
                  className={`sponsorFaqChevron ${isOpen ? "isRotated" : ""}`}
                />
              </button>

              {isOpen && (
                <div
                  className="sponsorFaqBody"
                  data-testid={`sponsor-faq-body-${i}`}
                >
                  {it.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SponsorshipPreviewTile({
  topic,
  sponsorCompany,
  sponsorLogo,
  tableName,
  moderator,
}) {
  return (
    <div className="sponsorPreviewTile">
      <div className="sponsorPreviewTag">Preview</div>

      <p className="sponsorPreviewCircle">Founders</p>
      <p className="sponsorPreviewTopic">{topic || "Your sponsored table"}</p>

      {tableName && (
        <p className="sponsorPreviewTableName">Sponsored as · "{tableName}"</p>
      )}

      {moderator && (
        <p className="sponsorPreviewModerator">
          <Mail size={9} className="sponsorPreviewModeratorIcon" /> Moderated by{" "}
          {moderator}
        </p>
      )}

      <div className="sponsorPreviewBottom">
        <span className="sponsorPreviewBottomLabel">Sponsored by</span>

        <span className="sponsorPreviewBrandPill">
          {sponsorLogo ? (
            <img src={sponsorLogo} alt="" className="sponsorPreviewBrandLogo" />
          ) : (
            <span className="sponsorPreviewBrandLogoFallback" />
          )}
          {sponsorCompany}
        </span>

        <span className="sponsorPreviewMomentPill">▶ moment</span>
      </div>
    </div>
  );
}
